import type { ReactNode } from 'react';
import { Box, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { NextIcon, PreviousIcon } from './icons';
import { useTranslation } from 'react-i18next';
import { formatCivilDate } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { addMonths, monthStart } from '@/features/planned/calendarDates';
import { localeWeekdayOrder, weekdayName } from '@/features/planned/recurrence/recurrence';
import { resolveTokens } from '@/theme/theme';

export interface MonthCalendarEntry {
  date: string;
  key: string;
  render: () => ReactNode;
}

interface MonthCalendarProps {
  month: { year: number; monthIndex: number };
  entries: MonthCalendarEntry[];
  onNavigate: (year: number, monthIndex: number) => void;
  emptyLabel: string;
  /** Extra control in a day's header (planned +). */
  dayAccessory?: (civil: string) => ReactNode;
}

/**
 * Generic month grid (`md`+) / chronological list (`xs`). Extracted from 0009's occurrence
 * calendar so meetings reuse the same shell.
 */
export function MonthCalendar({
  month,
  entries,
  onNavigate,
  emptyLabel,
  dayAccessory,
}: MonthCalendarProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const theme = useTheme();
  const md = useMediaQuery(theme.breakpoints.up('md'));
  const { year, monthIndex } = month;
  const weekdays = localeWeekdayOrder(locale);
  const title = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(
    new Date(year, monthIndex, 1),
  );
  const byDay = new Map<string, MonthCalendarEntry[]>();
  for (const entry of entries) {
    const list = byDay.get(entry.date) ?? [];
    list.push(entry);
    byDay.set(entry.date, list);
  }

  const days: string[] = [];
  const first = new Date(year, monthIndex, 1);
  const startPad = weekdays.findIndex((d) => {
    const map: Record<string, number> = { SUN: 0, MON: 1, TUE: 2, WED: 3, THU: 4, FRI: 5, SAT: 6 };
    return map[d] === first.getDay();
  });
  for (let i = 0; i < (startPad < 0 ? 0 : startPad); i += 1) {
    days.push('');
  }
  const lastDate = new Date(year, monthIndex + 1, 0).getDate();
  for (let d = 1; d <= lastDate; d += 1) {
    days.push(monthStart(year, monthIndex).slice(0, 8) + String(d).padStart(2, '0'));
  }

  const grouped = [...byDay.entries()].sort(([a], [b]) => a.localeCompare(b));

  return (
    <Box>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton
          aria-label={t('planned.monthPrev')}
          onClick={() => {
            const next = addMonths(year, monthIndex, -1);
            onNavigate(next.year, next.monthIndex);
          }}
        >
          <PreviousIcon size={24} />
        </IconButton>
        <Typography variant="h2" sx={{ flex: 1 }}>
          {title}
        </Typography>
        <Typography
          component="button"
          variant="body2"
          onClick={() => {
            const now = new Date();
            onNavigate(now.getFullYear(), now.getMonth());
          }}
          sx={{
            border: 0,
            background: 'none',
            cursor: 'pointer',
            color: (theme) => resolveTokens(theme).link,
            font: 'inherit',
          }}
        >
          {t('planned.monthToday')}
        </Typography>
        <IconButton
          aria-label={t('planned.monthNext')}
          onClick={() => {
            const next = addMonths(year, monthIndex, 1);
            onNavigate(next.year, next.monthIndex);
          }}
        >
          <NextIcon size={24} />
        </IconButton>
      </Stack>

      {md ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: 1,
          }}
        >
          {weekdays.map((d) => (
            <Typography key={d} variant="caption" sx={{ color: 'text.secondary' }}>
              {weekdayName(d, locale, 'short')}
            </Typography>
          ))}
          {days.map((civil, i) => (
            <Box
              key={civil || `pad-${i}`}
              sx={{
                minHeight: 88,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                p: 0.5,
              }}
            >
              {civil !== '' && (
                <>
                  <Stack
                    direction="row"
                    sx={{ justifyContent: 'space-between', alignItems: 'center' }}
                  >
                    <Typography variant="caption">{Number(civil.slice(8, 10))}</Typography>
                    {dayAccessory?.(civil)}
                  </Stack>
                  <Stack spacing={0.5}>
                    {byDay.get(civil)?.map((entry) => (
                      <Box key={entry.key}>{entry.render()}</Box>
                    ))}
                  </Stack>
                </>
              )}
            </Box>
          ))}
        </Box>
      ) : grouped.length === 0 ? (
        <Typography>{emptyLabel}</Typography>
      ) : (
        <Stack spacing={2}>
          {grouped.map(([day, rows]) => (
            <Box key={day}>
              <Typography variant="subtitle2" component="h3">
                {formatCivilDate(day, locale)}
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                {rows.map((entry) => (
                  <Box key={entry.key}>{entry.render()}</Box>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      )}
    </Box>
  );
}
